/*
 Adobe Systems Incorporated(r) Source Code License Agreement
 Copyright(c) 2005 Adobe Systems Incorporated. All rights reserved.

 Please read this Source Code License Agreement carefully before using
 the source code.

 Adobe Systems Incorporated grants to you a perpetual, worldwide, non-exclusive,
 no-charge, royalty-free, irrevocable copyright license, to reproduce,
 prepare derivative works of, publicly display, publicly perform, and
 distribute this source code and such derivative works in source or
 object code form without any attribution requirements.

 The name "Adobe Systems Incorporated" must not be used to endorse or promote products
 derived from the source code without prior written permission.

 You agree to indemnify, hold harmless and defend Adobe Systems Incorporated from and
 against any loss, damage, claims or lawsuits, including attorney's
 fees that arise or result from your use or distribution of the source
 code.

 THIS SOURCE CODE IS PROVIDED "AS IS" AND "WITH ALL FAULTS", WITHOUT
 ANY TECHNICAL SUPPORT OR ANY EXPRESSED OR IMPLIED WARRANTIES, INCLUDING,
 BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 FOR A PARTICULAR PURPOSE ARE DISCLAIMED.  ALSO, THERE IS NO WARRANTY OF
 NON-INFRINGEMENT, TITLE OR QUIET ENJOYMENT.  IN NO EVENT SHALL MACROMEDIA
 OR ITS SUPPLIERS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO,
 PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS;
 OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR
 OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOURCE CODE, EVEN IF
 ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
export class DictionaryUtil {

    /**
     * Returns an Array of all keys within the specified Map.
     *
     * @param map The Map instance whose keys will be returned.
     *
     * @return Array of keys contained within the Map.
     */
    public static getKeys(map: Map<any, any>): any[] {
        let keys: any[] = [];
        if (map === null) {
            return [];
        }
        map.forEach(
            (v: any, k: any, m: Map<any, any>) => {
                keys.push(k);
            }
        );

        return keys;
    }

    /**
     * Returns an Array of all values within the specified Map.
     *
     * @param map The Map instance whose values will be returned.
     *
     * @return Array of values contained within the Map.
     */
    public static getValues(map: Map<any, any>): any[] {
        let values: any[] = [];
        if (map === null) {
            return [];
        }
        map.forEach(
            (v: any, k: any, m: Map<any, any>) => {
                values.push(v);
            }
        );

        return values;
    }

    /**
     * Check whether the given Map contains the given key.
     *
     * @param map the Map to check for a key.
     * @param key the key to look up in the map.
     *
     * @return <code>true</code> if the map contains the given key, <code>false</code> if not.
     */
    public static containsKey(map: Map<any, any>, key: any): boolean {
        return map.has(key);
    }

    /**
     * Check whether the given map contains the given value.
     *
     * @param map the Map to check for a value.
     * @param value the value to look up in the Map.
     *
     * @return <code>true</code> if the map contains the given value, <code>false</code> if not.
     */
    public static containsValue(map: Map<any, any>, value: any): boolean {
        let result: boolean = false;
        if (map === null) {
            return false;
        }
        map.forEach(
            (v: any, k: any, m: Map<any, any>) => {
                if (v === value) {
                    result = true;
                }
            }
        );

        return result;
    }

    /**
     * Clones the given Map.
     *
     * @param original the Map to be cloned
     *
     * @returns a new Map instance.
     */
    public static clone(original: Map<any, any>): Map<any, any> {
        return new Map<any, any>(original);
    }

    /**
     * Get the key that is mapped to the value element.
     *
     * @param map the Map to check for a value.
     * @param value the value to look for inside the Map.
     *
     * @returns the key that is mapped to the value, or null otherwise.
     */
    public static getKeyForValue(map: Map<any, any>, value: any): any {
        let key: any = null;

        map.forEach(
            (v: any, k: any, m: Map<any, any>) => {
                if (v === value) {
                    key = k;
                }
            }
        );

        return key;
    }

    /**
     * Concatenates any number of mappers.
     *
     * @param mappers separated list of mappers.
     *
     * @return concatenated Map.
     */
    public static concatDictionaries(...mappers: Map<any, any>[]): Map<any, any> {
        let concatenatedMap: Map<any, any> = new Map<any, any>();
        let map: Map<any, any>;

        for (let i: number = 0; i < mappers.length; i++) {
            map = mappers[i];
            map.forEach(
                (v: any, k: any, m: Map<any, any>) => {
                    concatenatedMap.set(k, v);
                }
            );
        }

        return concatenatedMap;
    }

    /**
     * Get the size of the Map
     *
     * @param map the Map to check for its size.
     *
     * @returns size of the Map.
     */
    public static getDictionaryLength(map: Map<any, any>): number {
        return map.size;
    }

    /**
     * Check if the Map is empty.
     *
     * @param map the Map to check if its empty.
     *
     * @returns <code>true</code> if the map is empty, <code>false</code> if not.
     */
    public static isEmpty(map: Map<any, any>): boolean {
        return map.size === 0;
    }
}
